import Select from '@databases/atlaskit/Select';
import { type DatabaseField } from '@databases/types/database';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import {
  type CustomOrderVariant,
  type SortByDirection,
  type SortCategory,
  dataModelToSortByDirection,
  getLabelMessage,
  getSortCategory,
  sortByDirectionToDataModel
} from './sort-direction-utils.ts';
import { useEntryDetailsResolvedField } from './useEntryDetailsResolvedField.ts';
import { useJiraFieldColumnType } from './useJiraFieldColumnType.ts';

export {
  dataModelToSortByDirection,
  getLabelMessage,
  getSortCategory,
  sortByDirectionToDataModel,
  useEntryDetailsResolvedField,
  useJiraFieldColumnType
};
export type { CustomOrderVariant, SortByDirection, SortCategory };

type SortByDirectionOption = { label: string; value: SortByDirection };

export interface SortByDirectionPickerProps {
  field: DatabaseField;
  id: string;
  onChange: (value: SortByDirection) => void;
  resolvedTargetField?: DatabaseField;
  jiraColumnType?: string;
  spacing?: 'compact' | 'default';
  value: SortByDirection;
}

export const SortByDirectionPicker = ({
  field,
  id,
  onChange,
  resolvedTargetField,
  jiraColumnType,
  spacing,
  value
}: SortByDirectionPickerProps) => {
  const { formatMessage } = useIntl();

  const sortCategory = useMemo(
    () => getSortCategory(field, { resolvedTargetField, jiraColumnType }),
    [field, resolvedTargetField, jiraColumnType]
  );

  const options: SortByDirectionOption[] = useMemo(
    () => [
      {
        label: formatMessage(getLabelMessage({ direction: 'ascending', sortCategory })),
        value: 'ascending'
      },
      {
        label: formatMessage(getLabelMessage({ direction: 'descending', sortCategory })),
        value: 'descending'
      }
    ],
    [sortCategory, formatMessage]
  );

  const handleChange = useCallback(
    (option: SortByDirectionOption | null) => {
      return onChange(option?.value || 'ascending');
    },
    [onChange]
  );

  return (
    <Select<SortByDirectionOption>
      inputId={id}
      classNamePrefix="sort-direction-options"
      onChange={handleChange}
      options={options}
      spacing={spacing}
      value={options.find((option) => option.value === value)}
      shouldPreventEscapePropagation
    />
  );
};
